import { PayloadAction } from '@reduxjs/toolkit';
import { commandActions, ISelect } from 'core/slices/commands';
import { ISceneEditorState, sceneEditorActions } from 'core/slices/sceneEditor';
import { EntityTypes } from 'constants/entities';
import { updateState } from 'core/actions/product/state';
import { selectOfflineId } from 'core/selectors/admin/offlineId';
import { selectActionDAO } from 'core/selectors/product/action/action';
import { createRequest } from 'core/services/admin';
import { all, takeLatest, fork, select, put, call, takeEvery } from 'redux-saga/effects';
import { RootState } from 'types';
import { ActionBehaviour, ActionType, IActionDAO, IActionEntityBasic } from 'types/domain/action';
import { actionEditorActions, IActionEditorState, IEditState } from '.';
import { selectActionEditor } from './selectActionEditor';
import { Mode } from 'core/slices/session';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { interact, IPlayerState, updatePlayerState } from 'core/slices/player';
import { selectSceneEditor } from 'core/slices/sceneEditor/selectSceneEditor';
import { SceneType } from 'types/domain/scene';
import { createNextAction } from 'core/actions/admin/data';
import { selectSceneConfig } from 'core/selectors/product/scene/sceneConfig';

function* dropSaga() {
  const actionEditor: IActionEditorState = yield select((state: RootState) => state.actionEditor);
  const action: IActionDAO = yield select(state => selectActionDAO(state, undefined));
  const sceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  const offlineId: number = yield select(selectOfflineId);

  const entity = {
    id: offlineId,
    offlineId,
    actionId: action.id,
    positionId: offlineId,
    position: {
      id: offlineId,
      offlineId,
      x: actionEditor.position[0],
      y: actionEditor.position[1],
      z: actionEditor.position[2],
    },
    scale: actionEditor.scale ? actionEditor.scale[0] : 1,
    order: action.entities.length,
    type: actionEditor.dropId,
    status: 0,
  };

  if (action.scene?.type === SceneType.VIDEO) {
    const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
    if (sceneConfig && sceneConfig.start < videoEl.currentTime) {
      yield put(
        yield call(createNextAction, {
          type: ActionType.INTERACTIVE,
          behaviour: ActionBehaviour.PAUSE,
          entities: [entity] as IActionEntityBasic[],
        }),
      );
      return;
    }
  }

  yield put(
    yield call(createRequest, {
      type: EntityTypes.ACTION_ENTITY,
      parentEntityType: EntityTypes.ACTION,
      parentEntityId: action.id,
      entity,
    }),
  );
  yield put(yield call(updateState, { type: EntityTypes.ACTION_ENTITY, id: offlineId }));
  yield put(yield call(actionEditorActions.dragIn, null));
  yield put(yield call(actionEditorActions.edit, { text: true }));
}

function* selectSaga(action: PayloadAction<ISelect>) {}

function* delectSaga() {
  const actionEditor: IActionEditorState = yield select(selectActionEditor);
  const mode: Mode = yield select(selectSessionMode);
  if (actionEditor && !actionEditor?.drawing && mode === Mode.ADMIN) {
    const actionEntityId = yield select((state: RootState) => state.state[EntityTypes.ACTION_ENTITY]);

    if (actionEntityId) yield put(yield call(updateState, { type: EntityTypes.ACTION_ENTITY, id: null }));
  }
}

function* editSaga(action: PayloadAction<IEditState>) {
  const state: IPlayerState = yield select((state: RootState) => state.core.player.state);
  if (!state.paused) yield put(yield call(updatePlayerState, { paused: true, cuplrit: 'editSaga' }));
}

function* interactSaga() {
  const mode: Mode = yield select(selectSessionMode);

  if (mode !== Mode.ADMIN) return;

  const actionEditor: IActionEditorState = yield select(selectActionEditor);
  const sceneEditor: ISceneEditorState = yield select(selectSceneEditor);
  if (actionEditor?.enabled || Object.values(actionEditor?.edit).some(v => v))
    yield put(yield call(actionEditorActions.deselect));

  if (sceneEditor?.edit.mediaEdit) yield put(yield call(sceneEditorActions.edit, { mediaEdit: false }));

  const paused = yield select((state: RootState) => state.core.player.state.paused);
  if (!paused) yield put(yield call(updatePlayerState, { paused: true, culprit: 'interacted' }));
}

function* watchEdit() {
  yield takeLatest(actionEditorActions.edit, editSaga);
}

function* watchDrop() {
  yield takeLatest(actionEditorActions.drop, dropSaga);
}

function* watchDeselect() {
  yield takeLatest(actionEditorActions.deselect, delectSaga);
}

function* watchSelect() {
  yield takeLatest(commandActions.select, selectSaga);
}

function* watchInteract() {
  yield takeEvery(interact, interactSaga);
}

export function* actionEditorSaga() {
  yield all([fork(watchDrop), fork(watchSelect), fork(watchDeselect), fork(watchEdit), fork(watchInteract)]);
}
